import React, { useState, useRef, useCallback } from 'react';
import { IconZoomIn, IconMaximize, IconPhotoOff, IconSparkles } from '@tabler/icons-react';

/**
 * ImageMagnifier
 * Komponen Kaca Pembesar (Magnifying Glass / Loupe Lens) interaktif untuk perhiasan emas & berlian.
 * Mendukung interaksi mouse hover (desktop) dan touch drag (mobile).
 */
export default function ImageMagnifier({
  src,
  alt = 'Foto Barang',
  zoomLevel = 2.5,
  lensSize = 140,
  maxHeight = '320px',
  onPreviewClick,
  className = '',
}) {
  const [showLens, setShowLens] = useState(false);
  const [lensPos, setLensPos] = useState({ x: 0, y: 0 });
  const [bgPos, setBgPos] = useState({ x: 0, y: 0 });
  const [imgDimensions, setImgDimensions] = useState({ width: 0, height: 0 });
  const [isTouchDevice, setIsTouchDevice] = useState(false);
  const [imageError, setImageError] = useState(false);
  const containerRef = useRef(null);
  const imgRef = useRef(null);

  // Update posisi kaca pembesar berdasarkan koordinat client
  const updatePosition = useCallback(
    (clientX, clientY, isTouch = false) => {
      if (!containerRef.current || !imgRef.current) return;

      const rect = imgRef.current.getBoundingClientRect();
      const containerRect = containerRef.current.getBoundingClientRect();

      // Posisi pointer relatif terhadap elemen gambar
      let x = clientX - rect.left;
      let y = clientY - rect.top;

      // Batasi koordinat dalam area gambar
      x = Math.max(0, Math.min(x, rect.width));
      y = Math.max(0, Math.min(y, rect.height));

      // Posisi lensa relatif terhadap container
      // Di perangkat sentuh (HP), angkat lensa sedikit ke atas agar tidak tertutup jempol
      const touchOffsetY = isTouch ? -45 : 0;
      const lensX = x + (rect.left - containerRect.left);
      const lensY = y + (rect.top - containerRect.top) + touchOffsetY;

      // Hitung background position untuk zoom
      const bgX = (x / rect.width) * 100;
      const bgY = (y / rect.height) * 100;

      setLensPos({ x: lensX, y: lensY });
      setBgPos({ x: bgX, y: bgY });
      setImgDimensions({ width: rect.width, height: rect.height });
    },
    []
  );

  // Event Mouse (Desktop)
  const handleMouseEnter = () => {
    setIsTouchDevice(false);
    setShowLens(true);
  };

  const handleMouseMove = (e) => {
    updatePosition(e.clientX, e.clientY, false);
  };

  const handleMouseLeave = () => {
    setShowLens(false);
  };

  // Event Touch (Mobile)
  const handleTouchStart = (e) => {
    if (e.touches.length === 1) {
      setIsTouchDevice(true);
      setShowLens(true);
      updatePosition(e.touches[0].clientX, e.touches[0].clientY, true);
    }
  };

  const handleTouchMove = (e) => {
    if (e.touches.length === 1) {
      // Mencegah scroll layar ketika sedang memeriksa detail barang dengan kaca pembesar
      if (e.cancelable) {
        e.preventDefault();
      }
      updatePosition(e.touches[0].clientX, e.touches[0].clientY, true);
    }
  };

  const handleTouchEnd = () => {
    setShowLens(false);
  };

  if (!src || imageError) {
    return (
      <div
        className={`w-full flex flex-col items-center justify-center rounded-2xl border-2 border-dashed border-slate-200 dark:border-slate-700 bg-slate-50/80 dark:bg-slate-800/40 p-8 text-center text-slate-400 ${className}`}
        style={{ minHeight: maxHeight }}
      >
        <div className="w-12 h-12 rounded-2xl bg-slate-100 dark:bg-slate-800 flex items-center justify-center mb-2 text-slate-400 dark:text-slate-500">
          <IconPhotoOff size={24} stroke={1.5} />
        </div>
        <span className="text-xs font-semibold text-slate-500 dark:text-slate-400">
          Tidak Ada Foto Barang
        </span>
        <span className="text-[11px] text-slate-400 dark:text-slate-500 mt-0.5">
          Foto fisik barang belum diunggah
        </span>
      </div>
    );
  }

  const radius = lensSize / 2;

  return (
    <div className={`relative flex flex-col select-none ${className}`}>
      {/* Container Utama Foto */}
      <div
        ref={containerRef}
        onMouseEnter={handleMouseEnter}
        onMouseMove={handleMouseMove}
        onMouseLeave={handleMouseLeave}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        onTouchCancel={handleTouchEnd}
        className="relative overflow-hidden rounded-2xl bg-gradient-to-b from-slate-50 to-slate-100/70 dark:from-slate-900 dark:to-slate-950 border border-slate-200/90 dark:border-slate-800 shadow-inner flex items-center justify-center cursor-crosshair group touch-none"
        style={{ minHeight: '220px', maxHeight }}
      >
        {/* Foto Barang Utama */}
        <img
          ref={imgRef}
          src={src}
          alt={alt}
          onError={() => setImageError(true)}
          className="max-h-[300px] w-auto max-w-full object-contain mx-auto p-2 transition-transform duration-200 pointer-events-auto"
          style={{ maxHeight }}
          draggable={false}
        />

        {/* Lensa Kaca Pembesar (Magnifying Glass Loupe) */}
        {showLens && imgDimensions.width > 0 && (
          <div
            className="absolute pointer-events-none rounded-full border-[3px] border-white shadow-[0_10px_35px_rgba(0,0,0,0.45),0_0_0_1px_rgba(217,119,6,0.3)] ring-2 ring-amber-500/80 z-30 transition-transform duration-75 ease-out overflow-hidden"
            style={{
              width: `${lensSize}px`,
              height: `${lensSize}px`,
              left: `${lensPos.x - radius}px`,
              top: `${lensPos.y - radius}px`,
              backgroundImage: `url("${src}")`,
              backgroundRepeat: 'no-repeat',
              backgroundSize: `${imgDimensions.width * zoomLevel}px ${imgDimensions.height * zoomLevel}px`,
              backgroundPosition: `${bgPos.x}% ${bgPos.y}%`,
              backgroundColor: '#fff',
            }}
          >
            {/* Crosshair & Glass Glow Highlight Effect */}
            <div className="absolute inset-0 rounded-full bg-gradient-to-tr from-white/0 via-white/20 to-white/60 pointer-events-none" />
            <div className="absolute inset-0 rounded-full shadow-[inset_0_0_18px_rgba(0,0,0,0.35)] pointer-events-none" />
            {/* Target Reticle (Titik Tengah Fokus) */}
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-2 h-2 rounded-full border border-amber-500/70 bg-white/40 pointer-events-none" />
          </div>
        )}

        {/* Top Badges: Category & Zoom Pill */}
        <div className="absolute top-3 left-3 flex items-center gap-1.5 z-20 pointer-events-none">
          {showLens && (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-lg bg-amber-500 text-white text-[10px] font-extrabold shadow-sm animate-pulse">
              <IconZoomIn size={12} />
              {zoomLevel}x Loupe
            </span>
          )}
        </div>

        {/* Top Right: Fullscreen Lightbox Button (jika onPreviewClick diberikan) */}
        {onPreviewClick && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onPreviewClick();
            }}
            className="absolute top-3 right-3 z-20 p-2 rounded-xl bg-white/90 dark:bg-slate-900/90 backdrop-blur-md text-slate-600 dark:text-slate-300 hover:text-amber-600 dark:hover:text-amber-400 hover:bg-white dark:hover:bg-slate-800 border border-slate-200/80 dark:border-slate-700 shadow-sm transition-all cursor-pointer"
            title="Buka Foto Penuh (Ukuran Asli)"
          >
            <IconMaximize size={16} />
          </button>
        )}
      </div>
    </div>
  );
}
